import { Link, useNavigate } from "react-router-dom";
import { Search, HelpCircle, Settings, Moon, Sun, LogOut, ChevronDown, Contrast } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { UserAvatar } from "@/components/UserAvatar";
import { currentTheme } from "@/lib/theme";

export function Navbar() {
  const { user, logout } = useAuth();
  const { highContrast, toggleHighContrast } = useAccessibility();
  const navigate = useNavigate();
  const { setTheme } = useAuth();
  const [dark, setDark] = useState(() => currentTheme() === "dark");

  useEffect(() => {
    const sync = () => setDark(currentTheme() === "dark");
    window.addEventListener("kindler-theme", sync);
    return () => window.removeEventListener("kindler-theme", sync);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-md bg-[var(--brand-navy)] border-b border-amber-900/40 text-amber-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/" className="font-sacred text-2xl tracking-wide" data-testid="brand-logo">
          Kindler
        </Link>
        <div className="hidden md:flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-amber-50 hover:bg-amber-50/10 hover:text-amber-200" data-testid="nav-session-planner">
                Session Planner <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => navigate("/planner/new")} data-testid="nav-new-session">New Session</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/sessions")} data-testid="nav-continue-session">Continue a Session</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" className="text-amber-50 hover:bg-amber-50/10 hover:text-amber-200" onClick={() => navigate("/library")} data-testid="nav-prayer-library">
                Prayer Library
              </Button>
          <Button variant="ghost" className="text-amber-50 hover:bg-amber-50/10 hover:text-amber-200" onClick={() => navigate("/ministry")} data-testid="nav-my-ministry">
            My Ministry
          </Button>
          {user?.role === "admin" && (
            <Button variant="ghost" className="text-amber-50 hover:bg-amber-50/10 hover:text-amber-200" onClick={() => navigate("/admin/library")} data-testid="nav-manage-library">
              Manage Library
            </Button>
          )}
          {user?.role === "admin" && (
            <Button variant="ghost" className="text-amber-50 hover:bg-amber-50/10 hover:text-amber-200" onClick={() => navigate("/admin/organizations")} data-testid="nav-organizations">
              Organizations
            </Button>
          )}
          <Button variant="ghost" className="text-amber-50 hover:bg-amber-50/10 hover:text-amber-200" onClick={() => navigate("/sessions")} data-testid="nav-my-sessions">
            My Sessions
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" className="rounded-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-amber-50 h-9 w-9" onClick={() => toast.info("Search coming soon")} data-testid="nav-search-button">
            <Search className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="rounded-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-amber-50 h-9 w-9" onClick={() => toast.info("Help center coming soon")} data-testid="nav-help-button">
            <HelpCircle className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className={`rounded-full h-9 w-9 hover:bg-amber-50/10 text-amber-50 ${highContrast ? "ring-2 ring-amber-300" : ""}`} onClick={toggleHighContrast} aria-pressed={highContrast} title="Toggle high contrast" data-testid="nav-high-contrast-toggle">
            <Contrast className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="rounded-full h-9 w-9 hover:bg-amber-50/10 text-amber-50" onClick={() => setTheme(dark ? "light" : "dark")} title={dark ? "Switch to light mode" : "Switch to dark mode"} data-testid="nav-theme-toggle">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full ring-2 ring-[var(--brand-gold)] ring-offset-1 transition-transform hover:scale-105" data-testid="nav-user-menu">
                  <UserAvatar name={user.name} email={user.email} picture={user.picture} className="h-9 w-9" testId="nav-user-avatar" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm text-muted-foreground" data-testid="nav-user-name">{user.name}</div>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="nav-branding-settings">
                  <Settings className="h-3.5 w-3.5 mr-2" /> Branding Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-button">
                  <LogOut className="h-3.5 w-3.5 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
  );
}
